// @ts-strict-ignore
import {
  dateCell,
  moneyCell,
  pillCell,
  readonlyTextCell,
  textCell,
} from "@dashboard/components/Datagrid/customCells/cells";
import { GetCellContentOpts } from "@dashboard/components/Datagrid/Datagrid";
import { AvailableColumn } from "@dashboard/components/Datagrid/types";
import { getStatusColor } from "@dashboard/misc";
import { SubscriptionListUrlSortField } from "@dashboard/orders/urls";
import {
  mapSubscriptionStatus,
  SubscriptionRow,
} from "@dashboard/orders/views/OrderSubscriptionsList/OrderSubscriptionsList.utils";
import { getColumnSortDirectionIcon } from "@dashboard/utils/columns/getColumnSortDirectionIcon";
import { GridCell, Item } from "@glideapps/glide-data-grid";
import { DefaultTheme, useTheme } from "@saleor/macaw-ui-next";
import { IntlShape } from "react-intl";

import { columnsMessages } from "./messages";

export const subscriptionListStaticColumnAdapter = (
  emptyColumn: AvailableColumn,
  intl: IntlShape,
  sort: { sort?: SubscriptionListUrlSortField; asc?: boolean },
) =>
  [
    emptyColumn,
    {
      id: "order",
      title: intl.formatMessage(columnsMessages.order),
      width: 120,
    },
    {
      id: "customer",
      title: intl.formatMessage(columnsMessages.customer),
      width: 220,
    },
    {
      id: "status",
      title: intl.formatMessage(columnsMessages.status),
      width: 200,
    },
    {
      id: "nextPayment",
      title: intl.formatMessage(columnsMessages.nextPayment),
      width: 230,
    },
    {
      id: "interval",
      title: intl.formatMessage(columnsMessages.interval),
      width: 160,
    },
    {
      id: "amount",
      title: intl.formatMessage(columnsMessages.amount),
      width: 150,
    },
    {
      id: "originalOrderDate",
      title: intl.formatMessage(columnsMessages.originalOrderDate),
      width: 220,
    },
  ].map(column => ({
    ...column,
    icon: getColumnSortDirectionIcon(sort, column.id),
  }));

interface GetCellContentProps {
  columns: AvailableColumn[];
  rows: SubscriptionRow[];
}

function getDatagridRowDataIndex(row, removeArray) {
  return row + removeArray.filter(r => r <= row).length;
}

const COMMON_CELL_PROPS: Partial<GridCell> = { cursor: "pointer" };

function getStatusCellContent(
  currentTheme: DefaultTheme,
  rowData: SubscriptionRow,
) {
  const mapped = mapSubscriptionStatus(rowData.status);
  const color = getStatusColor({
    status: mapped.status,
    currentTheme,
  });
  return pillCell(mapped.label, color, COMMON_CELL_PROPS);
}

function getAmountCellContent(rowData: SubscriptionRow) {
  if (!rowData?.amount?.value || !rowData?.amount?.currency) {
    return readonlyTextCell("-");
  }
  const amount = Number(rowData.amount.value);
  if (Number.isNaN(amount)) {
    return readonlyTextCell("-");
  }
  return moneyCell(amount, rowData.amount.currency, COMMON_CELL_PROPS);
}

function getDateOrDashCell(value: string | null | undefined) {
  if (!value) {
    return readonlyTextCell("-");
  }
  const parsed = new Date(value).getTime();
  if (Number.isNaN(parsed)) {
    return readonlyTextCell("-");
  }
  return dateCell(value, COMMON_CELL_PROPS);
}

export const useGetCellContent = ({ columns, rows }: GetCellContentProps) => {
  const { theme } = useTheme();

  return (
    [column, row]: Item,
    { added, removed }: GetCellContentOpts,
  ): GridCell => {
    const columnId = columns[column]?.id;

    if (!columnId) {
      return readonlyTextCell("");
    }

    const rowData = added.includes(row)
      ? undefined
      : rows[getDatagridRowDataIndex(row, removed)];

    switch (columnId) {
      case "order":
        return readonlyTextCell(
          rowData?.saleor?.orderNumber ?? rowData?.saleor?.orderId ?? "-",
        );
      case "customer":
        return readonlyTextCell(rowData?.saleor?.userEmail ?? "-");
      case "status":
        return rowData ? getStatusCellContent(theme, rowData) : textCell("");
      case "nextPayment":
        return getDateOrDashCell(rowData?.nextPaymentDate);
      case "interval":
        return readonlyTextCell(rowData?.interval ?? "-");
      case "amount":
        return rowData ? getAmountCellContent(rowData) : textCell("");
      case "originalOrderDate":
        return getDateOrDashCell(rowData?.saleor?.orderCreated);
      default:
        return textCell("");
    }
  };
};
